import {
  type GeneratorKey,
  type LeadGenerationRunReadable,
  leadGenerationCampaignsRunsListOptions,
  TriggerEnum
} from '@crm/api'
import { useStyles } from '@crm/features/lead-generation/styles'
import {
  hasActiveRun,
  parseRunErrors,
  RUNS_POLL_INTERVAL
} from '@crm/features/lead-generation/utils'
import { defaultFilters } from '@crm/utils'
import { DateTimeCell, TableContainer } from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Flex, Typography } from 'antd'
import { useMemo } from 'react'

import { RunStatusTag } from '../RunStatusTag/RunStatusTag'

import type { TableColumnsType } from 'antd'

const { Text } = Typography

type Props = {
  generatorKey: GeneratorKey
}

type RunErrorAlertProps = {
  errorDetails?: string | string[]
}

const RunErrorAlert = ({ errorDetails }: RunErrorAlertProps) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const errors = parseRunErrors(errorDetails)
  const label = t('lead_generation.error_detail')

  return (
    <Alert
      type="error"
      message={errors.length > 1 ? `${label} (${errors.length})` : label}
      description={
        errors.length ? (
          <ul className={styles.errorList}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        ) : undefined
      }
    />
  )
}

// Kept at module scope so the table receives stable callbacks rather than a
// component redefined on every render of the card.
const rowExpandable = ({ errors, error_details }: LeadGenerationRunReadable) =>
  !!errors || !!parseRunErrors(error_details).length

const expandedRowRender = ({ error_details }: LeadGenerationRunReadable) => (
  <RunErrorAlert errorDetails={error_details} />
)

export const CampaignRuns = ({ generatorKey }: Props) => {
  const { t } = useLocales()
  const { params } = useQueryUpdate()

  const { data, isLoading } = useQuery({
    ...leadGenerationCampaignsRunsListOptions({
      path: { generator_key: generatorKey },
      query: { ...defaultFilters, ...params }
    }),
    // Re-evaluated after every fetch: polls only while a run is still active
    // and stops on its own once every visible run reaches a terminal status.
    refetchInterval: (query) =>
      hasActiveRun(query.state.data?.results) ? RUNS_POLL_INTERVAL : false
  })

  const columns: TableColumnsType<LeadGenerationRunReadable> = useMemo(
    () => [
      {
        title: '#',
        key: 'id',
        render: ({ id }) => <Text strong>{`#${id}`}</Text>
      },
      {
        title: t('lead_generation.status'),
        key: 'status',
        render: ({ status }) => <RunStatusTag status={status} />
      },
      {
        title: t('lead_generation.triggered_by'),
        key: 'triggered_by',
        render: ({ trigger, triggered_by }: LeadGenerationRunReadable) => {
          const isManual = trigger === TriggerEnum.MANUAL
          const triggerLabel = t(`lead_generation.${trigger}`, trigger)

          return (
            <Flex gap={4} wrap>
              <Text strong={isManual}>
                {isManual
                  ? triggered_by?.name || triggered_by?.email || '-'
                  : t('lead_generation.automatic')}
              </Text>
              <Text type="secondary">{`· ${triggerLabel}`}</Text>
            </Flex>
          )
        }
      },
      {
        title: t('lead_generation.started_at'),
        key: 'created_at',
        render: ({ created_at }) => <DateTimeCell date={created_at} />
      },
      {
        title: t('lead_generation.finished_at'),
        key: 'finished_at',
        render: ({ finished_at }) => <DateTimeCell date={finished_at} />
      },
      {
        title: t('lead_generation.candidates'),
        key: 'candidates_total',
        render: ({ candidates_total }) => candidates_total ?? 0
      },
      {
        title: t('lead_generation.created'),
        key: 'leads_created',
        render: ({ leads_created }) => <Text strong>{leads_created ?? 0}</Text>
      },
      {
        title: t('lead_generation.updated'),
        key: 'leads_updated',
        render: ({ leads_updated }) => leads_updated ?? 0
      },
      {
        title: t('lead_generation.rejected'),
        key: 'rejected',
        render: ({ rejected }) => rejected ?? 0
      },
      {
        title: t('lead_generation.errors'),
        key: 'errors',
        render: ({ errors }) => (
          <Text type={errors ? 'danger' : undefined} strong={!!errors}>
            {errors ?? 0}
          </Text>
        )
      }
    ],
    [t]
  )

  return (
    <Card
      title={
        <Flex align="center" gap={8} wrap>
          {t('lead_generation.runs')}
          <Text type="secondary">
            {t('lead_generation.runs_total', { total: data?.count ?? 0 })}
          </Text>
        </Flex>
      }
    >
      <TableContainer
        rowKey="id"
        data={data}
        columns={columns}
        loading={isLoading}
        isHoverAlternative
        showSearch={false}
        showSorterTooltip={false}
        expandable={{ rowExpandable, expandedRowRender }}
      />
    </Card>
  )
}
